import React, { useMemo } from "react";
import Container from "../../../../CommonElements/Container";
import SectionHeading from "../../../../CommonElements/SectionHeading";
import PrizeLine from "../../../../CommonElements/PrizeLine";
import PrizeTrophy from "../../../../CommonElements/PrizeTrophy";
import useReveal from "../../../../hooks/useReveal";
import { useCompetition } from "../../../../context/CompetitionContext";
import { useLanguage } from "../../../../context/LanguageContext";
import giftIcon from "../../../../assets/images/gift.svg";
import blurPrice from "../../../../assets/images/blurprice.svg";
import { formatDurationStat } from "../../../../utils/duration";

const PrizeCurrency = ({ currency, accent }) => (
  <div className={`prizes__currency prizes__currency--${accent}`}>
    <PrizeLine accent={accent} mirror />
    <span className="prizes__currency-text">{currency}</span>
    <PrizeLine accent={accent} />
  </div>
);

const PRIZE_PLACE_KEYS = {
  first: "prizes.firstPlace",
  second: "prizes.secondPlace",
  third: "prizes.thirdPlace",
  fourth: "prizes.fourthPlace",
  fifth: "prizes.fifthPlace",
  fivth: "prizes.fifthPlace",
  sixth: "prizes.sixthPlace",
  seventh: "prizes.seventhPlace",
  total: "prizes.totalPrizePool",
};

const PRIZE_PLACE_KEYS_BY_INDEX = [
  "prizes.firstPlace",
  "prizes.secondPlace",
  "prizes.thirdPlace",
  "prizes.fourthPlace",
  "prizes.fifthPlace",
  "prizes.sixthPlace",
  "prizes.seventhPlace",
];

const getPrizePlaceLabel = (prize, index, t) => {
  const key = PRIZE_PLACE_KEYS[prize.id] || PRIZE_PLACE_KEYS_BY_INDEX[index];
  if (key) return t(key);
  return prize.place || "";
};

const resolveStatValue = (stat, t) => {
  const durationLabel = formatDurationStat(stat, t);
  if (durationLabel) return durationLabel;

  if (stat.valueKey) {
    const translationKey = `prizes.${stat.valueKey}`;
    const translated = t(translationKey);
    if (translated !== translationKey) return translated;
  }
  return stat.value || "";
};

const resolveStat = (stat, t) => {
  const label = stat.key ? t(`prizes.${stat.key}`) : (stat.label || "");
  const value = resolveStatValue(stat, t);
  return {
    key: stat.key || label,
    label,
    value,
    highlight: Boolean(stat.highlight),
  };
};

const Prizes = () => {
  const { t } = useLanguage();
  const { competition, prizes } = useCompetition();
  const [revealRef, inView] = useReveal();

  const balance = competition.startingBalance || competition.stats?.[1]?.value || "";
  const prizePool = competition.totalPrizePool || "";
  const stats = useMemo(
    () => (competition.stats || []).map((stat) => resolveStat(stat, t)),
    [competition.stats, t]
  );

  return (
    <section className={`prizes${inView ? " is-in" : ""}`} id="prizes" ref={revealRef}>
      <Container>
        <header className="prizes__intro">
          <SectionHeading
            className="prizes__heading"
            title={t("prizes.title")}
            subtitle={t("prizes.subtitle", { prizePool, balance })}
          />
        </header>

        <div className="prizes__grid">
          {prizes.map((prize, index) => (
            <article
              className={`card card--prize card--prize-${prize.accent}`}
              key={prize.id}
            >
              <span className={`prizes__badge prizes__badge--${prize.accent}`}>
                {getPrizePlaceLabel(prize, index, t)}
              </span>
              <PrizeTrophy accent={prize.accent} placeIndex={index} />
              <p className="prizes__amount">{prize.amount}</p>
              <PrizeCurrency currency={prize.currency} accent={prize.accent} />
            </article>
          ))}

          <div className="prizes__total">
            <img
              className="prizes__total-blur"
              src={blurPrice}
              alt=""
              aria-hidden="true"
            />
            <img
              className="prizes__total-icon"
              src={giftIcon}
              alt=""
              width={32}
              height={32}
              aria-hidden="true"
            />
            <span className="prizes__total-label">{t("prizes.totalPrizePool")}</span>
            <strong className="prizes__total-amount">
              {competition.totalPrizePool}
            </strong>
            <PrizeCurrency currency={competition.currency} accent="total" />
          </div>
        </div>

        <div className="prizes__stats">
          <span className="prizes__stats-glow prizes__stats-glow--start" aria-hidden="true" />
          <span className="prizes__stats-glow prizes__stats-glow--mid" aria-hidden="true" />
          <span className="prizes__stats-glow prizes__stats-glow--end" aria-hidden="true" />
          {stats.map((stat) => (
            <div className="prizes__stat" key={stat.key}>
              <p className="prizes__stat-label">{stat.label}</p>
              <p
                className={`prizes__stat-value${
                  stat.highlight ? " prizes__stat-value--free" : ""
                }`}
              >
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Prizes;
